import { Lexer, type MarkedExtension, type MarkedOptions, type Token } from "marked"

// Markdown is parsed on the renderer's main thread, so a slow parse is a frozen
// window. marked is superlinear on some inputs WITHIN one inline run (a
// paragraph, a list item, a table cell): an email-like run such as `a.a.a…@`
// or `____…@` makes the GFM url/text rules rescan the rest of the run at every
// character, and a line of unmatched `*`/`~` delimiters (`**/*.ts` globs, a
// CODEOWNERS file pasted as prose) does the same in em/strong and del. Measured
// in Electron 41's V8: 20 KB of such a run costs 0.1–1 s, 40 KB 0.5–4.4 s, and
// the streaming view used to pay that on every tick.
//
// Nothing about those rules can be made linear from outside marked, but the
// cost is quadratic in the length of ONE run, so bounding the run bounds it:
// an inline source longer than INLINE_CHUNK is tokenized in consecutive pieces.
// A cut goes where it splits nothing (see `cut`) — a construct longer than a
// piece makes its piece longer instead — so ordinary prose renders exactly as
// before; only a run with no boundary at all, like the ones above, is cut hard.
//
// 2000 rather than less: Russian answers routinely write 1000+ characters per
// paragraph, and a paragraph that is never cut cannot render differently. The
// worst case does not pay for it: a boundary-less run is cut at half a piece,
// so 64 KB of `___…@` (the most a message gets before it turns plain) parses
// in ~0.5 s here, as it would at 1000, and in the ~25% duty cycle the
// streaming gate allows.
export const INLINE_CHUNK = 2000

// Beyond this a message is shown as plain text (with an opt-in to format it):
// ~30 printed pages is past what anyone reads as formatted chat, and the DOM
// work that follows a parse — sanitizing, file-chip decoration, diffing — grows
// with it no matter how fast the parser is. The line bound catches tool
// outputs (list/glob/grep) that are short per line but produce a file chip, an
// SVG icon included, for every path.
export const PLAIN_TEXT_CHARS = 64 * 1024
export const PLAIN_TEXT_LINES = 2000

export function oversized(text: string) {
  if (text.length > PLAIN_TEXT_CHARS) return true
  // Fewer characters than the line bound cannot hold that many lines.
  if (text.length <= PLAIN_TEXT_LINES) return false
  let lines = 0
  for (let i = text.indexOf("\n"); i !== -1; i = text.indexOf("\n", i + 1)) {
    if (++lines >= PLAIN_TEXT_LINES) return true
  }
  return false
}

const SENTENCE_END = /[.!?…;:][)"'»”]*\s/g
const DELIMITERS: Record<string, number> = { "*": 1, _: 2, "~": 4, $: 8 }
const WORD = /[\p{L}\p{N}]/u
const SPACE = /\s/
const TAG_NAME = /[a-zA-Z]/
const ATTRIBUTE = /[\w.:/-]/
const UNQUOTED = /[^"'=<>`]/

// How far past the window a piece may grow to keep one construct whole: a code
// span, link, emphasis or tag of up to ~16 KB renders as marked renders it.
// Beyond that — a construct that long, or far more likely a delimiter the
// count below pairs and marked does not — bounding the piece's cost wins.
const REACH = 8

// Past the raw HTML starting at text[at] — a tag or a comment, which marked
// passes through whole — or at + 1 when that `<` is only text, or past `end`
// when the tag is still open there. The same shape as marked's inline `tag`
// rule, loosely: outside a quoted or bare attribute value only name characters,
// `=`, `/` and whitespace; a bare `<` or a character no name has (prose such as
// `x<y, то`) means it was never a tag.
function tagEnd(text: string, at: number, end: number) {
  let i = text[at + 1] === "/" ? at + 2 : at + 1
  if (!TAG_NAME.test(text[i] ?? "")) return at + 1
  let quote = ""
  let value = false
  let bare = false
  for (; i < end; i++) {
    const char = text[i]!
    if (quote) {
      if (char === quote) quote = ""
      continue
    }
    if (char === ">") return i + 1
    if (SPACE.test(char)) {
      bare = false
      continue
    }
    if (value && (char === '"' || char === "'")) quote = char
    else if (value || bare) {
      if (!UNQUOTED.test(char)) return at + 1
      bare = true
    } else if (char !== "=" && !ATTRIBUTE.test(char)) return at + 1
    value = char === "="
  }
  return end + 1
}

// closed[i]: a cut before text[i] leaves nothing open behind it — no code
// span, link label or target, emphasis (`*`/`_`, doubled or not), `~~`, `$`
// math, or raw HTML tag or comment. Counting, not parsing: it only has to be
// right on well-formed text, and where it is not (a stray `[`, `$5`, a lone
// `2*3`) every later position reads as open and `cut` falls back to its plain
// order — what it did before this check existed.
function closedAt(text: string, end: number) {
  const closed = new Uint8Array(end + 1)
  let code = 0
  let label = 0
  let target = 0
  let runs = 0
  // The last `-->`, looked up at the first `<!--`: a comment with no closer is
  // text, and must not cost a scan to the end of the text at every opener.
  let comments: number | undefined
  closed[0] = 1
  for (let i = 0; i < end; ) {
    const char = text[i]!
    let next = i + 1
    if (char === "\\" && !code) next = i + 2
    else if (char === "`" || char in DELIMITERS) {
      while (next < end && text[next] === char) next++
      const size = next - i
      const bit = DELIMITERS[char] ?? 0
      // A code span closes only on a backtick run of the length that opened it.
      if (char === "`") code = !code ? size : code === size ? 0 : code
      else if (code) {
        // Delimiters inside a code span are text.
      } else if (char === "$") runs ^= bit
      else if (SPACE.test(text[i - 1] ?? " ") && SPACE.test(text[next] ?? " ")) {
        // Whitespace on both sides (`2 * 3`): it can neither open nor close.
      }
      // `_` inside a word (snake_case) is not emphasis; `*` is, even there.
      else if (char !== "_" || !WORD.test(text[i - 1] ?? "") || !WORD.test(text[next] ?? "")) {
        // `***` opens both: a double run and a single one.
        if (size >= 2) runs ^= bit
        if (size % 2) runs ^= bit << 4
      }
    } else if (!code && char === "<" && text.startsWith("<!--", i)) {
      comments ??= text.lastIndexOf("-->")
      const close = comments >= i + 2 ? text.indexOf("-->", i + 2) : -1
      if (close !== -1) next = close + 3
    } else if (!code && char === "<") next = tagEnd(text, i, end)
    else if (!code && char === "[") label++
    else if (!code && char === "]" && label > 0) {
      label--
      if (text[i + 1] === "(") {
        target = 1
        next = i + 2
      }
    } else if (!code && target > 0 && char === "(") target++
    else if (!code && target > 0 && char === ")") target--
    // Past `end` means `end` itself is inside what was skipped.
    const inside = next > end
    i = Math.min(next, end)
    closed[i] = inside || code || label || target || runs ? 0 : 1
  }
  return closed
}

// A hard break (two spaces or a backslash, then a newline) renders only whole
// and with text after it in its piece, so a cut that would split it, or end
// right after it, moves to where it starts: there it opens the next piece and
// still renders.
function beforeBreak(text: string, at: number) {
  let newline = at - 1
  if (text[newline] !== "\n") {
    newline = at
    while (text[newline] === " ") newline++
    if (text[newline] !== "\n") return at
  }
  let start = newline
  while (text[start - 1] === " ") start--
  if (newline - start >= 2) return Math.min(start, at)
  if (text[newline - 1] === "\\") return Math.min(newline - 1, at)
  return at
}

// The first boundary past `size` that closes everything (see `cut`), within
// REACH pieces' worth of text: after a whitespace character, or the end of the
// text itself.
function past(text: string, size: number) {
  const limit = Math.min(text.length, REACH * size)
  const closed = closedAt(text, limit)
  // Nothing closes before the first closed position, and often nothing does at
  // all (a stray `[` early in the piece): then there is no boundary to test.
  const first = closed.indexOf(1, size + 1)
  if (first === -1) return
  const region = text.slice(0, limit)
  const space = /\s/g
  space.lastIndex = first - 1
  for (let match = space.exec(region); match; match = space.exec(region)) {
    const end = beforeBreak(text, match.index + 1)
    if (closed[end]) return end
  }
  if (limit === text.length && closed[limit]) return limit
}

function cut(text: string, size: number) {
  const floor = Math.floor(size / 2)
  const window = text.slice(floor, size)
  const at = (offset: number) => floor + offset + 1
  // Preferred first: a line break, a sentence end, any space; latest first
  // within each. The first one that splits no construct wins.
  const lines = Array.from(window.matchAll(/\n/g), (match) => at(match.index))
  const sentences = Array.from(window.matchAll(SENTENCE_END), (match) => floor + match.index + match[0].length)
  const spaces = Array.from(window.matchAll(/\s/g), (match) => at(match.index))
  const candidates = [...lines.reverse(), ...sentences.reverse(), ...spaces.reverse()]
    .map((end) => beforeBreak(text, end))
    .filter((end) => end > 0)
  const closed = closedAt(text, size)
  const safe = candidates.find((end) => closed[end] === 1)
  if (safe !== undefined) return safe
  // Nothing in the window closes: one construct spans it — a code span, link,
  // emphasis or tag longer than half a piece. Splitting it would change how it
  // renders, so the piece grows to where it ends instead. Not for a run with
  // nothing open and no boundary at all (a 20 KB `a.a.a…@`): that is exactly
  // what the pieces exist to bound.
  const ahead = candidates.length || !closed[size] ? past(text, size) : undefined
  if (ahead !== undefined) return ahead
  // Nothing closes within reach either (a stray `[`, `$5` or `2*3` earlier in
  // the run), or there is nothing to keep whole: the plain order, and without
  // any boundary a hard cut. That one goes at half a piece: a run with no
  // whitespace for a whole window is what the bound exists for (its cost is
  // quadratic in the piece), and no position in it is better than another —
  // only never between the halves of a surrogate pair.
  if (candidates[0] !== undefined) return candidates[0]
  const code = text.charCodeAt(floor - 1)
  return code >= 0xd800 && code <= 0xdbff ? floor - 1 : floor
}

export function splitInline(src: string, size = INLINE_CHUNK) {
  const parts: string[] = []
  let rest = src
  while (rest.length > size) {
    const end = cut(rest, size)
    parts.push(rest.slice(0, end))
    rest = rest.slice(end)
  }
  if (rest || !parts.length) parts.push(rest)
  return parts
}

class BoundedLexer<P = string, R = string> extends Lexer<P, R> {
  override inlineTokens(src: string, tokens: Token[] = []) {
    if (src.length <= INLINE_CHUNK) return super.inlineTokens(src, tokens)
    // One token list across the pieces, so text tokens still merge at a cut.
    for (const part of splitInline(src)) super.inlineTokens(part, tokens)
    return tokens
  }
}

function lexBlock<P, R>(src: string, options?: MarkedOptions<P, R>) {
  return new BoundedLexer(options).lex(src)
}

function lexInline<P, R>(src: string, options?: MarkedOptions<P, R>) {
  return new BoundedLexer(options).inlineTokens(src)
}

// marked's static Lexer.lex/lexInline construct the base class, so the
// subclass has to be supplied through the provideLexer hook.
export const boundedLexer: MarkedExtension = {
  hooks: {
    provideLexer() {
      return this.block ? lexBlock : lexInline
    },
  },
}
